import React, { useState, useEffect } from 'react';
import { buildPaymentConfirmationEmailHtml, buildFlightEmailHtml, buildQuoteAcceptedEmailHtml, buildTeamInviteEmailHtml, buildVerifyAccountEmailHtml, buildOrgApprovalEmailHtml, buildItineraryProposalEmailHtml, buildFinalizePassengerProfilesEmailHtml, buildBookingSecuredEmailHtml, buildPassengerDetailsCompleteEmailHtml } from '@/utils/emailTemplates';
import { buildInvoiceEmailHtml } from '@utils/emailTemplates/clients/invoiceEmailTemplate';
import { buildAdvanceInvoicePaymentConfirmedEmailHtml } from '@/utils/emailTemplates/clients/advanceInvoicePaymentConfirmedEmailTemplate';
import { buildInvoicePaymentHoldEmailHtml } from '@utils/emailTemplates/clients/preauth/invoicePaymentHoldEmailTemplate';
import { buildQuotePaymentHoldEmailHtml } from '@utils/emailTemplates/clients/preauth/quotePaymentHoldEmailTemplate';
import { buildInvoicePaymentConfirmationEmailHtml } from '@utils/emailTemplates/clients/preauth/invoicePaymentConfirmationEmailTemplate';
import { buildQuotePaymentConfirmationEmailHtml } from '@utils/emailTemplates/clients/preauth/quotePaymentConfirmationEmailTemplate';
import { buildInvoicePaymentSecuredBookingEmailHtml } from '@utils/emailTemplates/clients/preauth/invoicePaymentSecuredBookingTemplate';
import { buildPrepaidBalanceClientEmailHtml } from '@utils/emailTemplates/brokers/prepaidInvoiceBalanceEmailTemplate';
import { ITINERARY_PROPOSAL_SAMPLE } from '@/utils/emailTemplates/data/itineraryProposalSample';
import { PAYMENT_CONFIRMATION_SAMPLE } from '@/utils/emailTemplates/data/paymentConfirmationSample';
import { QUOTE_ACCEPTED_SAMPLE } from '@/utils/emailTemplates/data/quoteAcceptedSample';
import { FLIGHT_TRACKING_SAMPLE } from '@/utils/emailTemplates/data/flightTrackingSample';
import { TEAM_INVITE_SAMPLE } from '@/utils/emailTemplates/data/teamInviteSample';
import { VERIFY_ACCOUNT_SAMPLE } from '@/utils/emailTemplates/data/verifyAccountSample';
import { FINALIZE_PASSENGER_PROFILES_SAMPLE } from '@/utils/emailTemplates/data/finalizePassengerProfilesSample';
import { PASSENGER_DETAILS_COMPLETE_SAMPLE } from '@/utils/emailTemplates/data/passengerDetailsCompleteSample';
import { ORG_APPROVAL_SAMPLE } from '@/utils/emailTemplates/data/orgApprovalSample';
import { BOOKING_SECURED_SAMPLE } from '@/utils/emailTemplates/data/bookingSecuredSample';
import { ADVANCE_INVOICE_SAMPLE } from '@/utils/emailTemplates/data/advanceInvoiceSample';
import { PRE_AUTH_PAYMENT_CONFIRMATION_SAMPLE } from '@/utils/emailTemplates/data/preAuthPaymentConfirmationSample';
import { PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE } from '@/utils/emailTemplates/data/preAuthPaymentConfirmationQuoteSample';

/**
 * Centralized email template preview page.
 * Access via /email-preview
 *
 * To add a new template:
 *  1. Add sample data to utils/emailTemplates/data/
 *  2. Provide a `build` function that returns the HTML string
 */

const EMAIL_TEMPLATES = [
  {
    id: 'advance-invoice',
    label: 'Advance Invoice',
    description: 'Sent to clients when an advance or additional services invoice is created',
    build: () => buildInvoiceEmailHtml(ADVANCE_INVOICE_SAMPLE),
  },
  {
    id: 'advance-invoice-payment-confirmed',
    label: 'Advance Invoice (Payment Confirmed)',
    description: 'Sent to clients after an advance or additional services invoice payment is confirmed',
    build: () => buildAdvanceInvoicePaymentConfirmedEmailHtml(ADVANCE_INVOICE_SAMPLE),
  },
  {
    id: 'advance-invoice-payment-hold',
    label: 'Advance Invoice (Payment Hold)',
    description: 'Sent to clients when a pre-authorization hold is placed on their card for an advance or additional services invoice',
    build: () => buildInvoicePaymentHoldEmailHtml(ADVANCE_INVOICE_SAMPLE),
  },
  {
    id: 'trip-payment-hold',
    label: 'Trip (Payment Hold)',
    description: 'Sent to clients when a pre-authorization hold is placed on their card for a trip booking',
    build: () => buildQuotePaymentHoldEmailHtml({
      quote: {
        ...PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.quote,
        operator_cost: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.selectedBidOption.operator_cost,
        broker_margin: 0,
        platform_fee: 0,
        federal_excise_tax: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.selectedBidOption.federal_excise_tax,
        segment_tax: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.selectedBidOption.segment_tax,
        card_fee: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.selectedBidOption.card_fee,
        total_due: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.selectedBidOption.total_price,
      },
      selectedBidOption: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.selectedBidOption,
      client: PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE.quote.client,
      organization: {},
    }),
  },
  {
    id: 'pre-auth-payment-confirmation',
    label: 'Payment Pre-Authorized',
    description: 'Sent to clients when a pre-authorization hold is placed on their card for an advance invoice',
    build: () => buildInvoicePaymentConfirmationEmailHtml(PRE_AUTH_PAYMENT_CONFIRMATION_SAMPLE),
  },
  {
    id: 'pre-auth-payment-confirmation-quote',
    label: 'Payment Pre-Authorized (Quote)',
    description: 'Sent to clients when a pre-authorization hold is placed on their card for a trip booking',
    build: () => buildQuotePaymentConfirmationEmailHtml(PRE_AUTH_PAYMENT_CONFIRMATION_QUOTE_SAMPLE),
  },
  {
    id: 'pre-auth-payment-secured-booking',
    label: 'Payment Pre-Authorized (Secured Booking)',
    description: 'Sent to clients when a pre-authorization hold secures their booking',
    build: () => buildInvoicePaymentSecuredBookingEmailHtml(PRE_AUTH_PAYMENT_CONFIRMATION_SAMPLE),
  },
  {
    id: 'prepaid-balance-client',
    label: 'Prepaid Balance Client',
    description: 'Sent to clients after a prepaid balance payment is received',
    build: () => buildPrepaidBalanceClientEmailHtml(ADVANCE_INVOICE_SAMPLE),
  },
  {
    id: 'itinerary-proposal',
    label: 'Itinerary Proposal',
    description: 'Sent to clients with trip quote details, legs, passengers, crew, and aircraft options',
    build: () => buildItineraryProposalEmailHtml(ITINERARY_PROPOSAL_SAMPLE),
  },
  {
    id: 'payment-confirmation',
    label: 'Payment Confirmation',
    description: 'Sent after a successful card payment – "Trip Paid by Client"',
    build: () => buildPaymentConfirmationEmailHtml(PAYMENT_CONFIRMATION_SAMPLE),
  },
  {
    id: 'quote-accepted',
    label: 'Quote Formally Accepted',
    description: 'Sent when the client signs the contract and proceeds to checkout',
    build: () => buildQuoteAcceptedEmailHtml(QUOTE_ACCEPTED_SAMPLE),
  },
  {
    id: 'flight-tracking',
    label: 'Flight Tracking',
    description: 'Sent when a flight is ready to be tracked',
    build: () => buildFlightEmailHtml(FLIGHT_TRACKING_SAMPLE),
  },
  {
    id: 'finalize-passenger-profiles',
    label: 'Finalize Passenger Profiles',
    description: 'Sent to clients to complete passenger profile information before a trip',
    build: () => buildFinalizePassengerProfilesEmailHtml(FINALIZE_PASSENGER_PROFILES_SAMPLE),
  },
  {
    id: 'passenger-details-complete',
    label: 'Passenger Details Complete',
    description: 'Sent to the broker after the client submits passenger details on the trip sheet',
    build: () => buildPassengerDetailsCompleteEmailHtml(PASSENGER_DETAILS_COMPLETE_SAMPLE),
  },
  {
    id: 'booking-secured',
    label: 'Booking Secured',
    description: 'Sent to clients when their booking is confirmed, with passenger details and price breakdown',
    build: () => buildBookingSecuredEmailHtml(BOOKING_SECURED_SAMPLE),
  },
  {
    id: 'team-invite',
    label: 'Team Invite',
    description: 'Sent when an admin invites a team member to join their organization',
    build: () => buildTeamInviteEmailHtml(TEAM_INVITE_SAMPLE),
  },
  {
    id: 'verify-account',
    label: 'Verify Your Account',
    description: 'Sent when a user registers and needs to verify their email address',
    build: () => buildVerifyAccountEmailHtml(VERIFY_ACCOUNT_SAMPLE),
  },
  {
    id: 'org-approval',
    label: 'Organization Approval',
    description: 'Sent when an organization account has been approved – platform config & KYC steps',
    build: () => buildOrgApprovalEmailHtml(ORG_APPROVAL_SAMPLE),
  },
];

// ─── Component ──────────────────────────────────────────────────────────────

export default function EmailPreview() {
  const [activeId, setActiveId] = useState(EMAIL_TEMPLATES[0].id);

  const active = EMAIL_TEMPLATES.find((t) => t.id === activeId) || EMAIL_TEMPLATES[0];

  const [emailHtml, setEmailHtml] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const result = await active.build();
      if (!cancelled) setEmailHtml(result);
    };
    run();
    return () => { cancelled = true; };
  }, [active]);

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(emailHtml);
  };

  const handleDownloadHtml = () => {
    const blob = new Blob([emailHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.id}-email.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <h1 className="text-lg font-semibold text-gray-900 shrink-0">Email Preview</h1>

        {/* Template selector */}
        <select
          value={activeId}
          onChange={(e) => setActiveId(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {EMAIL_TEMPLATES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>

        <span className="text-sm text-gray-500 hidden md:inline">{active.description}</span>

        <div className="flex-1" />

        <button
          onClick={handleCopyHtml}
          className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Copy HTML
        </button>
        <button
          onClick={handleDownloadHtml}
          className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
        >
          Download HTML
        </button>
      </div>

      {/* Email Preview */}
      <div className="flex justify-center py-8">
        <iframe
          key={activeId}
          srcDoc={emailHtml}
          title={`Email Preview – ${active.label}`}
          style={{
            width: '680px',
            height: '100vh',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: '#ffffff',
          }}
        />
      </div>
    </div>
  );
}
